import { Component, type ErrorInfo, type ReactNode } from "react";

const CHUNK_ERROR_RE =
  /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;
const RELOAD_FLAG = "bw:chunk-reloaded";

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Route-level boundary for lazy-chunk load failures. The usual trigger is a
 * deploy: a client still holding an old index.html navigates to a route whose
 * hashed chunk filename has changed and 404s. <Suspense> catches the pending
 * promise but NOT the rejection, so without this the whole app would unmount to
 * a blank screen. Recovery is a hard reload to fetch the fresh index.html +
 * current chunk names; a one-shot sessionStorage flag (reset on the next
 * successful mount via clearChunkReloadGuard) prevents a reload loop when the
 * failure is not deploy-related.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (CHUNK_ERROR_RE.test(error.message) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      return;
    }
    console.error("[ChunkErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-sm text-muted-foreground">
          <p>Something went wrong loading this page.</p>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem(RELOAD_FLAG);
              window.location.reload();
            }}
            className="rounded-md border border-border px-3 py-1.5 text-foreground transition-colors hover:bg-muted"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Reset the reload-loop guard once the app has mounted successfully. */
export function clearChunkReloadGuard(): void {
  sessionStorage.removeItem(RELOAD_FLAG);
}
